/**
 * Kontekst sesji w SPA (PLAN §5.2: `GET /auth/session` hydratuje aplikację po
 * odświeżeniu). Sesja żyje w cookie httpOnly — front zna wyłącznie to, co odda
 * endpoint, i nigdy nie trzyma tokenu w pamięci ani w localStorage.
 */
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { hasPermission, isRole, type Permission } from '@k1p4/shared'
import { ApiError, api, type SessionResponse } from '../api/client'
import { hasStoredLanguage } from '../i18n'

export interface AuthState {
  session: SessionResponse | null
  /** Trwa pierwsze pobranie sesji — guardy mają czekać, nie przekierowywać. */
  loading: boolean
  /** Zwraca świeżą sesję — ekran logowania rozstrzyga z niej cel nawigacji. */
  refresh: () => Promise<SessionResponse | null>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export const SESSION_QUERY_KEY = ['auth', 'session'] as const

async function fetchSession(): Promise<SessionResponse | null> {
  try {
    return await api.session()
  } catch (error) {
    // 401 to normalny stan gościa, nie błąd do pokazania
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    retry: false,
    staleTime: 30_000,
  })

  const refresh = useCallback(async (): Promise<SessionResponse | null> => {
    /* Pobranie zamiast unieważnienia — wywołujący potrzebuje wyniku od razu. */
    return queryClient.fetchQuery({
      queryKey: SESSION_QUERY_KEY,
      queryFn: fetchSession,
      staleTime: 0,
    })
  }, [queryClient])

  const logout = useCallback(async () => {
    try {
      await api.logout()
    } finally {
      queryClient.setQueryData(SESSION_QUERY_KEY, null)
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
    }
  }, [queryClient])

  /*
   * Język konta (fala 9, grupa 76): gdy urządzenie nie ma zapisanego wyboru,
   * interfejs przyjmuje `users.locale` — także po przejściu z apeksu na adres
   * firmy. Jawne kliknięcie przełącznika wygrywa i nadpisuje konto.
   */
  const { i18n } = useTranslation()
  const accountLocale = query.data?.user.locale
  useEffect(() => {
    if (!accountLocale || hasStoredLanguage()) return
    if ((i18n.resolvedLanguage ?? i18n.language) !== accountLocale) void i18n.changeLanguage(accountLocale)
  }, [accountLocale, i18n])

  const value = useMemo<AuthState>(
    () => ({
      session: query.data ?? null,
      loading: query.isPending,
      refresh,
      logout,
    }),
    [query.data, query.isPending, refresh, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** Sesja, gdy komponent stoi w drzewie AuthProvider — przełącznik języka bywa poza nim. */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext)
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth musi być użyte wewnątrz AuthProvider')
  return context
}

/** Czy zalogowany użytkownik ma którąkolwiek z podanych ról. */
export function useHasRole(...roles: readonly string[]): boolean {
  const { session } = useAuth()
  if (!session) return false
  return session.user.roles.some((role) => roles.includes(role))
}

/**
 * Czy sesja ma uprawnienie z macierzy §5.3. Serwer i tak sprawdza je przy
 * każdej mutacji — to jest po to, żeby nie pokazywać akcji, która skończy się
 * odmową (np. hr_admin nie zamyka okresów czasu pracy).
 */
export function useHasPermission(permission: Permission): boolean {
  const { session } = useAuth()
  if (!session) return false
  return hasPermission(session.user.roles.filter(isRole), permission)
}
